import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  HostListener,
  inject,
  signal,
} from '@angular/core';
import { CcfxButtonComponent } from '@ccfx/shared-lib/button';
import { TranslocoPipe } from '@jsverse/transloco';
import { TourService } from '../../services/tour.service';

interface SpotlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

@Component({
  selector: 'app-tour-overlay',
  standalone: true,
  imports: [TranslocoPipe, CcfxButtonComponent],
  templateUrl: './tour-overlay.html',
  styleUrl: './tour-overlay.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TourOverlayComponent {
  private readonly tourService = inject(TourService);

  protected readonly currentIndex = this.tourService.currentIndex;
  protected readonly steps = this.tourService.steps;
  protected readonly isActive = computed(() => this.currentIndex() !== null);
  protected readonly currentStep = computed(() => {
    const index = this.currentIndex();
    return index === null ? null : this.steps()[index];
  });
  protected readonly isLastStep = computed(
    () => this.currentIndex() !== null && this.currentIndex() === this.steps().length - 1,
  );

  protected readonly spotlight = signal<SpotlightRect | null>(null);
  protected readonly tooltipTop = signal(0);
  protected readonly tooltipLeft = signal(0);

  constructor() {
    effect(() => {
      // Scroll to and re-measure whenever the active step changes.
      this.currentStep();
      // Wait a tick for the DOM (e.g. section expansion) to settle before measuring.
      queueMicrotask(() => this.scrollToCurrentStep());
    });
  }

  private scrollToCurrentStep(): void {
    const target = this.getCurrentTarget();
    if (!target) {
      this.spotlight.set(null);
      return;
    }

    this.spotlight.set(null);
    target.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'nearest' });
    requestAnimationFrame(() => requestAnimationFrame(() => this.updatePosition()));
  }

  @HostListener('window:resize')
  @HostListener('window:scroll')
  protected updatePosition(): void {
    const step = this.currentStep();
    if (!step) {
      this.spotlight.set(null);
      return;
    }
    const target = this.getCurrentTarget();
    if (!target) {
      this.spotlight.set(null);
      return;
    }
    const rect = target.getBoundingClientRect();
    const padding = 8;
    this.spotlight.set({
      top: rect.top - padding,
      left: rect.left - padding,
      width: rect.width + padding * 2,
      height: rect.height + padding * 2,
    });

    const tooltipTop = Math.min(rect.bottom + 16, window.innerHeight - 180);
    const tooltipLeft = Math.min(Math.max(rect.left, 16), window.innerWidth - 340);
    this.tooltipTop.set(tooltipTop);
    this.tooltipLeft.set(tooltipLeft);
  }

  protected onNext(): void {
    this.tourService.next();
  }

  protected onPrev(): void {
    this.tourService.prev();
  }

  protected onSkip(): void {
    this.tourService.finish();
  }

  private getCurrentTarget(): Element | null {
    const step = this.currentStep();
    if (!step) {
      return null;
    }

    if (step.targetSelector) {
      const selectorTarget = document.querySelector(step.targetSelector);
      if (selectorTarget) {
        return selectorTarget;
      }
    }

    const idTarget = document.getElementById(step.elementId);
    if (idTarget) {
      return idTarget;
    }

    return document.querySelector(`[data-tour-step="${step.elementId}"]`);
  }
}
